import type React from 'react'
import { useCallback } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import {
  type UnitOfMeasure,
  VALIDATION_ERRORS,
  parseValueWithUnit,
  parsedDisplayDecimalPlaces,
  validateDimensionPoints,
} from '@/lib/units/unit-conversion'
import {
  buildUnifiedSelectionSingleShapeResizeUndo,
  hasUnifiedSelectionResizeTargetChanged,
} from '@/lib/transforms/unified-selection-resize'
import { mergeFreshShapes } from '../helpers/fresh-shapes'
import { createHistoryTxnId } from '../helpers/history-txn-id'
import {
  applyMultiSelectPanelResize,
  computeMultiSelectTargetSize,
} from '../helpers/multi-select-commit'
import {
  fallbackPanelResizeGeometry,
  nextDimensionsDisplay,
  panelDimensionRevertPoints,
} from '../helpers/panel-dimensions'
import {
  photoFillUpdatesForPanelResize,
  resolvePanelDimensionMaxPoints,
} from '../helpers/photo-fill-resize'
import { multiSelectionPanelWidthHeight } from '../helpers/selection-bounds'
import { isPanelShapeGeometryBlocked, shapesByIdFromManager } from '../helpers/selection-protection'
import { computeShapeResizeFromPanelDimension } from '../helpers/shape-resize'
import type {
  DimensionField,
  DimensionValues,
  DisplayPrecision,
  FormatForDisplay,
  PanelFieldErrors,
} from '../types/panel-fields'
import type { CommitBatchOrPersist, PersistShapesFromManager } from '../types/shape-commit'

type SetState<T> = React.Dispatch<React.SetStateAction<T>>

/**
 * W/H fields: typing, and committing a typed value as a resize of the selection. One validation
 * pipeline (blank → parse → photo/pasteboard range → precision) feeds both the multi-select and
 * the single-shape resize.
 */
export function useDimensionField(params: {
  activeObject: ShapeData | null
  activeObjects: ShapeData[]
  isMultiSelect: boolean
  unitOfMeasure: UnitOfMeasure
  dimensionsLinked: boolean
  shapeManagerRef?: React.RefObject<ShapeManagerRef | null>
  setActiveObject: (obj: ShapeData | null) => void
  setActiveObjects: (objs: ShapeData[]) => void
  formatForDisplay: FormatForDisplay
  setDimensions: SetState<DimensionValues>
  setFieldErrors: SetState<PanelFieldErrors>
  setDisplayPrecision: SetState<DisplayPrecision>
  persistShapesFromManager: PersistShapesFromManager
  commitBatchOrPersist: CommitBatchOrPersist
}) {
  const {
    activeObject,
    activeObjects,
    isMultiSelect,
    unitOfMeasure,
    dimensionsLinked,
    shapeManagerRef,
    setActiveObject,
    setActiveObjects,
    formatForDisplay,
    setDimensions,
    setFieldErrors,
    setDisplayPrecision,
    persistShapesFromManager,
    commitBatchOrPersist,
  } = params

  const handleDimensionChange = (field: DimensionField, value: string) => {
    if (dimensionsLinked) {
      setFieldErrors((prev) => ({ ...prev, width: undefined, height: undefined }))
      setDimensions({ width: value, height: value })
    } else {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }))
      setDimensions((prev) => ({ ...prev, [field]: value }))
    }
  }

  const commitDimension = useCallback(
    (field: DimensionField, value: string) => {
      const trimmed = value.trim()
      const mgr = shapeManagerRef?.current
      const showDimension = (formatted: string) =>
        setDimensions((prev) => nextDimensionsDisplay(prev, field, formatted, dimensionsLinked))
      const revertDimension = () =>
        showDimension(
          formatForDisplay(
            panelDimensionRevertPoints({ field, isMultiSelect, activeObjects, activeObject, mgr })
          )
        )

      if (trimmed === '') {
        revertDimension()
        setFieldErrors((prev) => ({ ...prev, [field]: VALIDATION_ERRORS.CANT_BE_BLANK }))
        return
      }

      const parsed = parseValueWithUnit(value, unitOfMeasure)

      if (parsed.error) {
        setFieldErrors((prev) => ({ ...prev, [field]: parsed.error }))
        revertDimension()
        return
      }

      const effectiveMaxPoints = resolvePanelDimensionMaxPoints({
        isMultiSelect,
        activeObject,
        field,
        linked: dimensionsLinked,
      })

      // W and H: validate range (min 0.01 pt, max pasteboard or photo-fill) with inline feedback
      const rangeError = validateDimensionPoints(parsed.points, unitOfMeasure, effectiveMaxPoints)

      if (rangeError) {
        // Unlike the blank/parse errors above, a linked range error marks both fields.
        setFieldErrors((prev) =>
          dimensionsLinked
            ? { ...prev, width: rangeError, height: rangeError }
            : { ...prev, [field]: rangeError }
        )
        revertDimension()
        return
      }

      const { points } = parsed
      const displayDp = parsedDisplayDecimalPlaces(parsed, unitOfMeasure)
      const formatted = formatForDisplay(points, field, displayDp)
      const finishCommit = () => {
        showDimension(formatted)
        setFieldErrors((prev) => ({ ...prev, width: undefined, height: undefined }))
      }

      if (dimensionsLinked) {
        setDisplayPrecision((prev) => ({ ...prev, width: displayDp, height: displayDp }))
      } else {
        setDisplayPrecision((prev) => ({ ...prev, [field]: displayDp }))
      }

      if (isMultiSelect && activeObjects.length > 0 && mgr) {
        const panelWh = multiSelectionPanelWidthHeight({
          shapes: activeObjects,
          shapeManager: mgr ?? null,
        })
        const target = computeMultiSelectTargetSize({
          current: panelWh,
          field,
          points,
          linked: dimensionsLinked,
        })

        if (
          !hasUnifiedSelectionResizeTargetChanged(
            panelWh.width,
            panelWh.height,
            target.width,
            target.height
          )
        ) {
          finishCommit()
          return
        }

        const mixedResizeHistoryTxnId = createHistoryTxnId('mixed-resize-panel')
        const batchItems = applyMultiSelectPanelResize({
          mgr,
          activeObjects,
          field,
          targetWidth: target.width,
          targetHeight: target.height,
          dimensionsLinked,
        })

        if (batchItems === null) {
          finishCommit()
          return
        }

        commitBatchOrPersist(batchItems, 'resize', 'update', mixedResizeHistoryTxnId)

        if (mgr) {
          setActiveObjects(mergeFreshShapes(activeObjects, mgr.getShapes()))
        }
      } else if (activeObject && mgr) {
        const shapesByIdForCommit = shapesByIdFromManager(mgr)
        const latest = shapesByIdForCommit.get(activeObject.id) ?? activeObject

        if (isPanelShapeGeometryBlocked(latest, shapesByIdForCommit)) {
          return
        }

        const before = {
          x: activeObject.x,
          y: activeObject.y,
          width: activeObject.width,
          height: activeObject.height,
        }
        const nextGeometry = computeShapeResizeFromPanelDimension({
          shape: activeObject,
          field,
          targetPoints: points,
          linked: dimensionsLinked,
        })
        const geometry =
          nextGeometry ??
          fallbackPanelResizeGeometry({
            shape: activeObject,
            field,
            points,
            linked: dimensionsLinked,
          })
        const updates = photoFillUpdatesForPanelResize({
          shape: activeObject,
          before,
          geometry,
          linked: dimensionsLinked,
        })

        mgr.updateShape(activeObject.id, updates)
        const fresh = mgr.getShapes().find((s) => s.id === activeObject.id)

        if (fresh) {
          setActiveObject(fresh)
          const undoMeta = buildUnifiedSelectionSingleShapeResizeUndo({
            before,
            after: { x: fresh.x, y: fresh.y, width: fresh.width, height: fresh.height },
            mode: dimensionsLinked ? 'any' : field,
          })

          if (undoMeta) {
            persistShapesFromManager([activeObject.id], 'resize', undoMeta)
          }
        } else {
          setActiveObject({ ...activeObject, ...updates } as ShapeData)
          persistShapesFromManager([activeObject.id], 'update')
        }
      }

      finishCommit()
    },
    [
      activeObject,
      activeObjects,
      isMultiSelect,
      unitOfMeasure,
      formatForDisplay,
      dimensionsLinked,
      shapeManagerRef,
      setActiveObject,
      persistShapesFromManager,
      commitBatchOrPersist,
      setActiveObjects,
      setDimensions,
      setFieldErrors,
      setDisplayPrecision,
    ]
  )

  return { handleDimensionChange, commitDimension }
}
